/**
 * Sprint Creation Modal Component
 * Based on monday.com sprint creation with enhanced MUI design
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  Chip,
  InputAdornment,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FlagIcon from '@mui/icons-material/Flag';
import dayjs, { Dayjs } from 'dayjs';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { createSprint, addSprintGoal } from '../../store/sprintSlice';
import { updateTask, fetchTasks } from '../../store/taskSlice';
import { showToast } from '../../store/uiSlice';
import './SprintCreationModal.scss';

interface SprintCreationModalProps {
  open: boolean;
  onClose: () => void;
  boardId: string;
}

interface GoalInput {
  id: string;
  description: string;
  targetValue?: number;
  currentValue?: number;
}

const SprintCreationModal: React.FC<SprintCreationModalProps> = ({
  open,
  onClose,
  boardId,
}) => {
  const dispatch = useAppDispatch();
  const { tasks } = useAppSelector((state) => state.tasks);
  // state.tasks.tasks is whichever board's tasks were last fetched anywhere
  // in the app - filtering by boardId as well as !sprintId is required so a
  // ticket from a board the user previously viewed doesn't show up as
  // "available to add" to a sprint being created on this board (the backend
  // now rejects that assignment outright, but showing it here at all is
  // misleading and was the actual mechanism behind the cross-board leakage).
  const backlogTasks = (tasks || []).filter(
    (task) => !task.sprintId && String(task.boardId) === String(boardId)
  );

  const [sprintName, setSprintName] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [durationWeeks, setDurationWeeks] = useState<number>(1);
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(6, 'day'));
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [goals, setGoals] = useState<GoalInput[]>([
    { id: '1', description: '', targetValue: undefined, currentValue: 0 },
  ]);
  const [loading, setLoading] = useState(false);

  const isAllSelected = backlogTasks.length > 0 && backlogTasks.every((task) => selectedTaskIds.includes(String(task.id)));
  const isSomeSelected = selectedTaskIds.length > 0 && !isAllSelected;

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedTaskIds([]);
    } else {
      setSelectedTaskIds(backlogTasks.map((task) => String(task.id)));
    }
  };

  const handleToggleTask = (taskId: string | number) => {
    const idStr = String(taskId);
    setSelectedTaskIds((prev) =>
      prev.includes(idStr) ? prev.filter((id) => id !== idStr) : [...prev, idStr]
    );
  };

  const handleStartDateChange = (newStart: Dayjs | null) => {
    setStartDate(newStart);
    if (newStart) {
      setEndDate(newStart.add(durationWeeks * 7 - 1, 'day'));
    }
  };

  const handleDurationChange = (weeks: number) => {
    setDurationWeeks(weeks);
    if (startDate) {
      setEndDate(startDate.add(weeks * 7 - 1, 'day'));
    }
  };

  const handleAddGoal = () => {
    setGoals([
      ...goals,
      {
        id: Date.now().toString(),
        description: '',
        targetValue: undefined,
        currentValue: 0,
      },
    ]);
  };

  const handleRemoveGoal = (id: string) => {
    if (goals.length > 1) {
      setGoals(goals.filter((goal) => goal.id !== id));
    }
  };

  const handleGoalChange = (
    id: string,
    field: keyof GoalInput,
    value: string | number
  ) => {
    setGoals(
      goals.map((goal) =>
        goal.id === id
          ? {
              ...goal,
              [field]: field === 'description' ? value : Number(value),
            }
          : goal
      )
    );
  };

  const handleCreateSprint = async () => {
    if (!sprintName.trim() || !startDate || !endDate) {
      return;
    }

    setLoading(true);

    try {
      // Filter out empty goals and convert to string array
      const validGoalDescriptions: string[] = goals
        .filter((goal) => goal.description.trim())
        .map((goal) => goal.description);

      const createdSprint = await dispatch(
        createSprint({
          name: sprintName,
          boardId,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          goal: validGoalDescriptions.length > 0 ? validGoalDescriptions[0] : undefined,
          goals: validGoalDescriptions.length > 0 ? validGoalDescriptions : undefined,
        })
      ).unwrap();

      if (createdSprint && createdSprint.id && validGoalDescriptions.length > 0) {
        for (const goalDesc of validGoalDescriptions) {
          try {
            await dispatch(
              addSprintGoal({
                sprintId: String(createdSprint.id),
                goal: { description: goalDesc } as any,
              })
            );
          } catch (goalError) {
            console.error('Failed to add individual sprint goal:', goalError);
          }
        }
      }

      // Assign selected backlog tasks to the newly created sprint
      if (createdSprint && createdSprint.id && selectedTaskIds.length > 0) {
        let failedCount = 0;
        for (const taskId of selectedTaskIds) {
          try {
            await dispatch(
              updateTask({
                taskId,
                data: { sprintId: String(createdSprint.id) },
              })
            ).unwrap();
          } catch (taskError) {
            failedCount += 1;
            console.error('Failed to assign backlog task to sprint:', taskError);
          }
        }
        if (failedCount > 0) {
          dispatch(
            showToast({
              message:
                failedCount === selectedTaskIds.length
                  ? 'Failed to add the selected tasks to the sprint'
                  : `${failedCount} of ${selectedTaskIds.length} selected tasks could not be added to the sprint`,
              type: 'error',
            })
          );
        }
        dispatch(fetchTasks(boardId));
      }

      // Reset form
      setSprintName('');
      setStartDate(dayjs());
      setEndDate(dayjs().add(2, 'week'));
      setSelectedTaskIds([]);
      setGoals([
        { id: '1', description: '', targetValue: undefined, currentValue: 0 },
      ]);

      onClose();
    } catch (error: any) {
      console.error('Failed to create sprint:', error);
      dispatch(
        showToast({
          message: (typeof error === 'string' ? error : error?.message) || 'Failed to create sprint',
          type: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      sprintName.trim() &&
      startDate &&
      endDate &&
      dayjs(endDate).isAfter(dayjs(startDate))
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        className="sprint-creation-modal"
      >
        <DialogTitle className="modal-header">
          <Box display="flex" alignItems="center" gap={1}>
            <FlagIcon color="primary" />
            <Typography variant="h6">Create New Sprint</Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            className="close-button"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="modal-content">
          {/* Sprint Name */}
          <Box className="form-section">
            <Typography variant="subtitle2" className="field-label">
              Sprint Name *
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g., Sprint 1, Q1 Sprint, Feature Release Sprint"
              value={sprintName}
              onChange={(e) => setSprintName(e.target.value)}
              variant="outlined"
              size="medium"
            />
          </Box>

          {/* Sprint Duration Preset */}
          <Box className="form-section">
            <Typography variant="subtitle2" className="field-label">
              Sprint Duration
            </Typography>
            <TextField
              select
              fullWidth
              value={durationWeeks}
              onChange={(e) => handleDurationChange(Number(e.target.value))}
              variant="outlined"
              size="small"
            >
              <MenuItem value={1}>Sprint 1 (1 Week)</MenuItem>
              <MenuItem value={2}>Sprint 2 (2 Weeks)</MenuItem>
              <MenuItem value={3}>Sprint 3 (3 Weeks)</MenuItem>
              <MenuItem value={4}>Sprint 4 (4 Weeks / 1 Month)</MenuItem>
            </TextField>
          </Box>

          {/* Date Range */}
          <Box className="form-section date-range">
            <Box className="date-field">
              <Typography variant="subtitle2" className="field-label">
                Start Date *
              </Typography>
              <DatePicker
                value={startDate}
                onChange={(newValue) => handleStartDateChange(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
              />
            </Box>

            <Box className="date-field">
              <Typography variant="subtitle2" className="field-label">
                End Date (Auto-calculated) *
              </Typography>
              <DatePicker
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                minDate={startDate || undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
              />
            </Box>
          </Box>

          {/* Sprint Duration Indicator */}
          {startDate && endDate && (
            <Box className="duration-indicator">
              <Chip
                label={`${dayjs(endDate).diff(dayjs(startDate), 'day')} days`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          )}

          {/* Sprint Goals */}
          <Box className="form-section goals-section">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="subtitle2" className="field-label">
                Sprint Goals (Optional)
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddGoal}
                size="small"
                variant="text"
              >
                Add Goal
              </Button>
            </Box>

            {goals.map((goal, index) => (
              <Box key={goal.id} className="goal-item">
                <Box className="goal-header">
                  <Typography variant="caption" color="text.secondary">
                    Goal {index + 1}
                  </Typography>
                  {goals.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveGoal(goal.id)}
                      className="remove-goal-btn"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <TextField
                  fullWidth
                  placeholder="e.g., Complete all high-priority features"
                  value={goal.description}
                  onChange={(e) =>
                    handleGoalChange(goal.id, 'description', e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                  className="goal-description"
                />

                <Box className="goal-metrics">
                  <TextField
                    type="number"
                    placeholder="Target"
                    value={goal.targetValue || ''}
                    onChange={(e) =>
                      handleGoalChange(goal.id, 'targetValue', e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">points</InputAdornment>
                      ),
                    }}
                    className="goal-metric-input"
                  />
                </Box>
              </Box>
            ))}
          </Box>

          {/* Select Backlog Tasks */}
          <Box className="form-section backlog-tasks-section" sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" className="field-label" sx={{ fontWeight: 600, mb: 0 }}>
                Select Backlog Tasks to Include (Optional - {selectedTaskIds.length} selected)
              </Typography>
              {backlogTasks.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isAllSelected}
                      indeterminate={isSomeSelected}
                      onChange={handleToggleSelectAll}
                    />
                  }
                  label={
                    <Typography variant="caption" fontWeight={600} color="#4f46e5">
                      {isAllSelected ? 'Deselect All' : 'Select All'}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              )}
            </Box>
            {backlogTasks.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No tasks currently in backlog.
              </Typography>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  maxHeight: 260,
                  overflowY: 'auto',
                  p: 1,
                  borderRadius: 2,
                  borderColor: '#cbd5e1',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.04)',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f5f9',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#94a3b8',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#64748b',
                  },
                }}
              >
                {backlogTasks.map((task) => {
                  const taskIdStr = String(task.id);
                  const isChecked = selectedTaskIds.includes(taskIdStr);
                  return (
                    <Box
                      key={task.id}
                      onClick={() => handleToggleTask(task.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        bgcolor: isChecked ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                        mb: 0.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleToggleTask(task.id)}
                        />
                        <Typography variant="body2" fontWeight={500}>
                          {task.title}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {task.priority && (
                          <Chip label={task.priority} size="small" variant="outlined" />
                        )}
                        {task.storyPoints !== undefined && task.storyPoints > 0 && (
                          <Chip label={`${task.storyPoints} pts`} size="small" color="primary" />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            )}
          </Box>

          {/* Info Message */}
          <Box className="info-message">
            <Typography variant="caption" color="text.secondary">
              You can add tasks to this sprint after creation, or start the
              sprint immediately to begin tracking.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions className="modal-actions">
          <Button onClick={onClose} variant="text" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateSprint}
            variant="contained"
            color="primary"
            disabled={!isFormValid() || loading}
          >
            {loading ? 'Creating...' : 'Create Sprint'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default SprintCreationModal;
